import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import './_discover.scss'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'
import type SwiperCore from 'swiper'
import { Keyboard, Mousewheel, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import * as SvgIcons from 'assets/svg/sprite'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import PreviewComments from 'features/Preview/PreviewComments/PreviewComments'
import SinglePostPreview from 'features/Preview/SinglePostPreview/SinglePostPreview'
import { useDiscoveryPostsFeed } from 'helpers/hooks'
import { getCachedBlob, prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { enqueuePostView } from 'helpers/trackingViews'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { useUserProfile } from 'store'
import { type IFile } from 'types/interfaces/IFile'
import { type IPost } from 'types/interfaces/ITypes'

import { DiscoverProfilePanel } from './DiscoverProfilePanel'

import 'swiper/css/bundle'

const DISCOVER_LIST_ID = 'all'
const DISCOVER_TYPE = 'all'

const PREFETCH_AHEAD = 1
const VIRTUAL_BEFORE = 1
const VIRTUAL_AFTER = 1
const FETCH_WHEN_REMAINING_POSTS = 2

const DISCOVER_PREV_ROUTE_KEY = 'discover_prev_route'

function collectPhotoUrls(post?: IPost | null): string[] {
  if (!post) return []

  const raw = (post.photos ?? [])
    .map((x: any) => x?.url)
    .filter(Boolean)

  return Array.from(new Set(raw))
}

function clampIndex(idx: number, len: number): number {
  if (!Number.isFinite(idx)) return 0
  if (len <= 0) return 0

  return Math.min(Math.max(idx, 0), len - 1)
}

function getPostIdParam(raw?: string): string | null {
    return raw ?? null
}

function isDiscoverPath(path: string): boolean {
  return path.startsWith('/discover')
}

const Discover = () => {
  const { reelId: reelIdParam } = useParams<{ reelId?: string }>()
  const routePostId = getPostIdParam(reelIdParam)

  const navigate = useNavigate()
  const location = useLocation()
  const modalContext = useModalContext()
  const userData = useUserProfile()
  const { t } = useTranslation()

  const isAuthed = !!userData?.id
  const isProfileRoute = location.pathname.endsWith('/profile')
    const [profileSlideActive, setProfileSlideActive] = useState(isProfileRoute)
  const [showControls, setShowControls] = useState(true)
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)
  const [allFiles, setAllFiles] = useState<IFile[]>([])
  const [currentStorySlide, setCurrentStorySlide] = useState<number | null>(0)
const [activeIndex, setActiveIndex] = useState(0)

  const [commentsActive, setCommentsActive] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [discoverBackPath, setDiscoverBackPath] = useState<string>(() => {
    return sessionStorage.getItem(DISCOVER_PREV_ROUTE_KEY) ?? '/'
  })

  const verticalSwiperRef = useRef<SwiperCore | null>(null)
  const horizontalSwiperRef = useRef<SwiperCore | null>(null)
  const didInitialAlignRef = useRef(false)
  const lastFetchSigRef = useRef<string>('')
  const viewTimerRef = useRef<number | null>(null)

  const paginationQuery = useDiscoveryPostsFeed({
    list: DISCOVER_LIST_ID,
    type: DISCOVER_TYPE,
    enabled: isAuthed,
  })

  const fetchNextPage = paginationQuery?.fetchNextPage
  const hasNextPage = paginationQuery?.hasNextPage ?? false
  const isFetchingNextPage = paginationQuery?.isFetchingNextPage ?? false
  const error = paginationQuery?.error
  const status = paginationQuery?.status ?? 'idle'
  const slidesSource = paginationQuery?.posts ?? []
  const slidesLen = slidesSource.length
  const isInitialLoading =
    slidesLen === 0 &&
    (
      paginationQuery?.isLoading ||
      paginationQuery?.isFetching
    )

  const isEmpty =
    !isInitialLoading &&
    status === 'success' &&
    slidesLen === 0

  useEffect(() => {
    const state = location.state as
      | {
          from?: string
          previousRoute?: string
          discoverBackPath?: string
        }
      | null
      | undefined

    const stateBackPath =
      state?.discoverBackPath ?? state?.previousRoute ?? state?.from

    if (!stateBackPath) return
    if (isDiscoverPath(stateBackPath)) return

    sessionStorage.setItem(DISCOVER_PREV_ROUTE_KEY, stateBackPath)
    setDiscoverBackPath(stateBackPath)
  }, [location.state])

const goBackFromDiscover = useCallback(() => {
  navigate(-1)
}, [navigate])


  const routeIndex = useMemo(() => {
    if (!routePostId) return -1

    return slidesSource.findIndex(post => post?.id === routePostId)
  }, [routePostId, slidesSource])

  const safeActiveIndex = useMemo(() => {
    if (routeIndex >= 0) return routeIndex

    return clampIndex(activeIndex, slidesLen)
  }, [routeIndex, activeIndex, slidesLen])

  const activePost = slidesSource[clampIndex(safeActiveIndex, slidesLen)] ?? null
  const activePostId = activePost?.id

  const hasPost =
      typeof activePostId === 'string' && activePostId.length > 0

  const shouldLockVertical =
    isProfileRoute || commentsActive || minimized || !showControls

  const initialSlide = clampIndex(routeIndex >= 0 ? routeIndex : 0, slidesLen)

  const openOptions = useCallback(() => {
    setIsOptionsNavOpen(true)
  }, [])

  const syncVerticalSwiperLock = useCallback(() => {
    const core = verticalSwiperRef.current
    if (!core) return

    core.allowTouchMove = !shouldLockVertical
    core.allowSlideNext = !shouldLockVertical
    core.allowSlidePrev = !shouldLockVertical

    const mousewheel = (core).mousewheel

    if (mousewheel?.enable && mousewheel?.disable) {
      if (shouldLockVertical) {
        mousewheel.disable()
      } else {
        mousewheel.enable()
      }
    }
  }, [shouldLockVertical])

  useEffect(() => {
    syncVerticalSwiperLock()
  }, [syncVerticalSwiperLock])

useEffect(() => {
  setProfileSlideActive(isProfileRoute)
}, [isProfileRoute, activePostId])

  useEffect(() => {
    const core = verticalSwiperRef.current
    if (!core) return
    if (!slidesLen) return

    requestAnimationFrame(() => {
      core.virtual?.update?.(true)
      syncVerticalSwiperLock()
    })
  }, [slidesLen, syncVerticalSwiperLock])


  const maybeFetchNext = useCallback(
    (nextActiveIndex: number) => {
      if (!fetchNextPage) return
      if (!hasNextPage) return
      if (isFetchingNextPage) return
      if (!slidesLen) return

      const remaining = slidesLen - 1 - nextActiveIndex
      if (remaining > FETCH_WHEN_REMAINING_POSTS) return

      const sig = `${nextActiveIndex}:${slidesLen}`
      if (lastFetchSigRef.current === sig) return

      lastFetchSigRef.current = sig
      fetchNextPage()
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, slidesLen]
  )

useEffect(() => {
  if (didInitialAlignRef.current) return
  if (!slidesSource.length) return

  const core = verticalSwiperRef.current
  if (!core) return

  didInitialAlignRef.current = true

  const target = routeIndex >= 0 ? routeIndex : 0

  requestAnimationFrame(() => {
    if (core.activeIndex !== target) {
      core.slideTo(target, 0)
    }

    setActiveIndex(target)
    syncVerticalSwiperLock()
  })
  // intentionally NOT depending on slidesSource object
}, [slidesLen, routeIndex, syncVerticalSwiperLock])

const goBackFromDiscoverProfile = useCallback(() => {
  if (!activePostId) {
    navigate('/discover', { replace: true })
    return
  }

  horizontalSwiperRef.current?.slideTo(0, 0)

  navigate(`/discover/reels/${activePostId}`, {
    replace: true,
  })
}, [activePostId, navigate])

  const prefetchSignature = useMemo(() => {
    if (!slidesLen) return 'empty'

    const idx = clampIndex(safeActiveIndex, slidesLen)
    const ids: Array<string | number> = []

    const currentPost = slidesSource[idx]
    if (currentPost?.id) ids.push(currentPost.id)

    for (let step = 1; step <= PREFETCH_AHEAD; step++) {
      const nextPost = slidesSource[idx + step]
      if (nextPost?.id) ids.push(nextPost.id)
    }

    return `${idx}:${ids.join(',')}`
  }, [slidesSource, slidesLen, safeActiveIndex])

  useEffect(() => {
    if (!slidesLen) return

    const idx = clampIndex(safeActiveIndex, slidesLen)
    const controller = new AbortController()
    const { signal } = controller

    const urlsSet = new Set<string>()

    collectPhotoUrls(slidesSource[idx]).forEach(url => urlsSet.add(url))

    for (let step = 1; step <= PREFETCH_AHEAD; step++) {
      const nextPost = slidesSource[idx + step]
      if (!nextPost) continue

      collectPhotoUrls(nextPost).forEach(url => urlsSet.add(url))
    }

    const urls = Array.from(urlsSet)

    urls.forEach(url => {
      if (!url) return
      if (getCachedBlob(url)) return

      prefetchImageToBlob(url, signal).catch(() => {})
    })

    return () => controller.abort()
  }, [prefetchSignature, slidesSource, slidesLen, safeActiveIndex])

  useEffect(() => {
    if (!activePost) return

    emitter.emit(EventsEmitter.STOP_ALL_AUDIO_POSTS)

    if (activePost.is_purchased) {
      const allData: IFile[] = [
        ...(activePost.photos ?? []),
        ...(activePost.videos ?? []),
        ...(activePost.sounds ?? []),
      ].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

      setAllFiles(allData)
      return
    }

    setAllFiles([])
  }, [activePost])

  useEffect(() => {
    const handlePreviewBack = (event: Event) => {
      const customEvent = event as CustomEvent<{ handled: boolean }>

      customEvent.detail.handled = true
      goBackFromDiscover()
    }

    window.addEventListener('preview-main-back', handlePreviewBack)

    return () => {
      window.removeEventListener('preview-main-back', handlePreviewBack)
    }
  }, [goBackFromDiscover])

  useEffect(() => {
    if (!isAuthed) return
    if (!hasPost) return
    if (!userData?.id) return
    if (!activePostId) return

    const creatorId = activePost?.user_id ?? activePost?.user?.id
    const isOther = !!creatorId && creatorId !== userData.id

    if (!isOther) return

    if (viewTimerRef.current != null) {
      window.clearTimeout(viewTimerRef.current)
      viewTimerRef.current = null
    }

    viewTimerRef.current = window.setTimeout(() => {
      enqueuePostView(activePostId)
    }, 1000)

    return () => {
      if (viewTimerRef.current != null) {
        window.clearTimeout(viewTimerRef.current)
        viewTimerRef.current = null
      }
    }
  }, [
    isAuthed,
    hasPost,
    activePostId,
    userData?.id,
    activePost?.user_id,
    activePost?.user?.id,
  ])

  const viewerOptions = useMemo(() => {
    if (!hasPost || !activePostId) return []

    return [
      {
        icon: <SvgIcons.IconOptionsPlaylist />,
        text: t('addToList'),
        disabled: allFiles.length === 0,
        action: () => {
          setIsOptionsNavOpen(false)

          modalContext.addModal(
            '',
            <AddToPlaylist
              itemId={activePostId}
              itemType='post'
              closeFn={() => modalContext.clearModal()}
            />,
            true,
            true
          )
        },
      },
    ]
  }, [hasPost, activePostId, t, allFiles.length, modalContext])

  if (error) console.log(error)

  if (status === 'error') {
    return <p>Error: {(error as any)?.message}</p>
  }

  return (
    <div className="discoverModal discoverModal--active">
    <div className="discoverModal__wrapper">
        {hasPost && activePostId && isOptionsNavOpen && (
          <OptionsDropdown
            role=""
            content="post"
            isOptionsNavOpen={isOptionsNavOpen}
            setIsOptionsNavOpen={setIsOptionsNavOpen}
            postId={activePostId}
            options={viewerOptions}
            deleteFn={() => {
              if (isOptionsNavOpen) setIsOptionsNavOpen(false)

              modalContext.clearModal()
            }}
            withoutBottomNavigation
          />
        )}

        {slidesLen > 0 ? (
          <Swiper
            modules={[Keyboard]}
            keyboard={{
              enabled: true,
              onlyInViewport: true,
            }}
            direction="horizontal"
            slidesPerView={1}
            allowTouchMove
            resistance
            resistanceRatio={0}
            touchReleaseOnEdges={false}
            threshold={0}
            shortSwipes
            longSwipes
            longSwipesRatio={0.25}
            touchStartPreventDefault={false}
            className="discover-horizontal-swiper"
            initialSlide={isProfileRoute ? 1 : 0}
onSliderMove={(horizontalSwiper: SwiperCore) => {
  if (horizontalSwiper.activeIndex !== 0) return
  if (profileSlideActive) return

  const translate = horizontalSwiper.translate

  if (translate < -20) {
    setProfileSlideActive(true)
  }
}}

onSlideChangeTransitionStart={(horizontalSwiper: SwiperCore) => {
  const goingToProfile = horizontalSwiper.activeIndex === 1
  setProfileSlideActive(goingToProfile)
}}
onSlideChangeTransitionEnd={(horizontalSwiper: SwiperCore) => {
  horizontalSwiperRef.current = horizontalSwiper

  if (!activePostId) return

  const nextPath =
    horizontalSwiper.activeIndex === 1
      ? `/discover/reels/${activePostId}/profile`
      : `/discover/reels/${activePostId}`

  if (location.pathname !== nextPath) {
    navigate(nextPath, { replace: true })
  }
}}
          >
            <SwiperSlide className="discover-horizontal-slide">
<Swiper
  modules={[Keyboard, Virtual, Mousewheel]}
  mousewheel={{
    enabled: !shouldLockVertical,
    forceToAxis: true,
    sensitivity: 0.5,
    releaseOnEdges: false,
  }}
  virtual={{
    enabled: true,
    addSlidesBefore: VIRTUAL_BEFORE,
    addSlidesAfter: VIRTUAL_AFTER,
  }}
  onInit={(core: SwiperCore) => {
    verticalSwiperRef.current = core

    core.el?.setAttribute('tabindex', '0')
    core.el?.focus()

    const idx =
      typeof (core as any).activeIndex === 'number'
        ? (core as any).activeIndex
        : initialSlide

    setActiveIndex(prev => (prev === idx ? prev : idx))

    ;(core as any)?.virtual?.update?.(true)

    requestAnimationFrame(() => {
      syncVerticalSwiperLock()
    })
  }}
  initialSlide={initialSlide}
  style={{ width: '100%', height: '100%' }}
  className="mySwiper"
  longSwipes={false}
  longSwipesRatio={0.25}
  shortSwipes
  direction="vertical"
  speed={600}
  keyboard={{
    enabled: !shouldLockVertical,
    onlyInViewport: true,
  }}
  allowTouchMove={!shouldLockVertical}
  touchStartPreventDefault
  passiveListeners={false}
  resistance
  resistanceRatio={0}
  touchReleaseOnEdges={false}
  slidesPerView={1}
  slidesPerGroup={1}
  centeredSlides={false}
  pagination={false}
  onSlideChangeTransitionStart={() => {
    if (!showControls) setShowControls(true)
  }}
  onReachEnd={(swiper: SwiperCore) => {
    const lastIndex = slidesLen - 1

    if (!hasNextPage && swiper.activeIndex > lastIndex) {
      swiper.slideTo(lastIndex, 0)
      setActiveIndex(lastIndex)
    }
  }}
  onSlideChange={(ctx: any) => {
    const rawIndex = ctx?.activeIndex ?? 0
    const nextIndex = clampIndex(rawIndex, slidesLen)

    if (rawIndex !== nextIndex) {
      ctx.slideTo(nextIndex, 0)
      return
    }

    setActiveIndex(nextIndex)

    const currentPost = slidesSource[nextIndex]
    if (!currentPost?.id) return

    const nextPath = `/discover/reels/${currentPost.id}`

    if (location.pathname !== nextPath) {
      navigate(nextPath, { replace: true })
    }

    maybeFetchNext(nextIndex)

    const storyPosts = slidesSource.filter(post => post?.stories?.length > 0)

    const currentStoryIndex = storyPosts.findIndex(
      post => post?.id === currentPost?.id
    )

    const nextStorySlide = currentStoryIndex >= 0 ? currentStoryIndex + 1 : 0

    setCurrentStorySlide(prev =>
      prev === nextStorySlide ? prev : nextStorySlide
    )
  }}
  noSwiping={!showControls || commentsActive || minimized}
>
                {slidesSource.map((post, idx) => {
                  const isActivePost = post?.id === activePostId

                  return (
                    <SwiperSlide
                      key={post?.id ?? idx}
                      virtualIndex={idx}
                      className={
                        !showControls || minimized ? 'swiper-no-swiping' : ''
                      }
                    >
                      <PostPreviewWrapper>
                        <SinglePostPreview
                          post={post}
                          setCommentsActive={setCommentsActive}
                          showControls={showControls}
                          setIsOptionsNavOpen={setIsOptionsNavOpen}
                          onOpenOptions={openOptions}
                          onClose={goBackFromDiscover}
                          setCurrentStorySlide={setCurrentStorySlide}
                          allPosts={slidesSource}
                          activeStorySlide={currentStorySlide}
                          minimizeFn={(_fileIndex: number, _tt: string) => {
                            if (minimized && !showControls) {
                                setShowControls(true)
                            }

                            setCurrentStorySlide(null)
                            setMinimized(prev => !prev)
                          }}
                          toggleControls={() => setShowControls(v => !v)}
                          isFirstPost={idx === 0}
                          hasNextPage={!!hasNextPage}
                          isInViewport={isActivePost}
                          isLastPost={idx === slidesLen - 1}
                          independentPreviewVisibility={true}
                        />
                      </PostPreviewWrapper>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </SwiperSlide>

            <SwiperSlide className="discover-horizontal-slide">
<DiscoverProfilePanel
  username={activePost?.user?.username}
  previewUser={activePost?.user}
  showFeed={activePost?.user?.show_feed}
  enabled={
    profileSlideActive &&
    !!activePost?.user?.username
  }
  onBack={goBackFromDiscoverProfile}
/>
            </SwiperSlide>
          </Swiper>
) : (
  <DiscoverLoadingShell>
    {isInitialLoading ? (
      <div className="discover-loader">
        <BeatLoader size={12} color="#999" />
      </div>
    ) : isEmpty ? (
      <div className="discover-empty">
        <p>No Posts Currently</p>

        <button
          type="button"
          className="discover-empty__close"
          onClick={goBackFromDiscover}
        >
          Close
        </button>
      </div>
    ) : null}
  </DiscoverLoadingShell>
)}
        {activePost && !activePost.stories?.length && !isProfileRoute && (
          <PreviewComments
            commentsActive={commentsActive}
            setCommentsActive={setCommentsActive}
            activeCommentsPostId={activePost.id}
            commentCount={activePost.comment_count}
            isMyPost={userData?.id === activePost.user?.id}
          />
        )}
      </div>
    </div>
  )
}

export default Discover

const PostPreviewWrapper = styled.div`
  height: 100dvh;
`

const DiscoverLoadingShell = styled.div`
  width: 100%;
  min-height: 100dvh;
  background: #000;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 950px) {
    min-height: calc(100dvh - 7rem);
    border-radius: 2.5rem;
    overflow: hidden;
  }
`